// Ficha de detalle de un feedback (vista alumno, read-only).

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { StarRating } from '@/components/molecules/StarRating';
import { Header } from '@/components/organisms/Header';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import { getFeedback, type Feedback } from '@/services/feedbacks';
import styles from './MisFeedbacks.module.scss';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

interface ScoreLine {
  label: string;
  value: number | null;
}

export function FeedbackDetail() {
  const { bookingId: bookingIdStr } = useParams<{ bookingId: string }>();
  const { state } = useAuth();
  const userId = state.status === 'authenticated' ? state.user.id : null;
  const bookingId = bookingIdStr ? Number(bookingIdStr) : NaN;

  const [item, setItem] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isInteger(bookingId) || bookingId < 1 || !userId) {
      setLoading(false);
      setError('Parámetros inválidos');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFeedback(bookingId, userId)
      .then((f) => { if (!cancelled) setItem(f); })
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [bookingId, userId]);

  const scores: ScoreLine[] = item ? [
    { label: 'Pronunciation', value: item.pronunciation },
    { label: 'Fluency',       value: item.fluency },
    { label: 'Structures',    value: item.structures },
    { label: 'Vocabulary',    value: item.vocabulary },
  ] : [];

  return (
    <>
      <Header title="Feedback" />
      <main className={styles.main}>
        <Link to="/mis-feedbacks" className={styles.backLink}>
          <Icon name="chevron-left" size={16} /> Volver
        </Link>

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : !item ? (
          <div className={styles.empty}>
            <Icon name="star" size={32} />
            <h3>Este feedback no está disponible todavía</h3>
            <p>Tu profe aún no lo ha publicado. Cuando lo complete, aparecerá aquí.</p>
          </div>
        ) : (
          <>
            <header className={styles.singleHeader}>
              <h2 className={styles.singleTitle}>{item.lesson_title ?? 'Clase'}</h2>
              <p className={styles.singleSubtitle}>
                {formatDate(item.booking_scheduled_at)} · con {item.teacher_name}
              </p>
              <div className={styles.singleBadges}>
                {item.attendance && (
                  <Badge tone={item.attendance === 'present' ? 'success' : item.attendance === 'late' ? 'warning' : 'neutral'}>
                    {item.attendance === 'present' ? 'Presente' : item.attendance === 'late' ? 'Tarde' : 'Ausente'}
                  </Badge>
                )}
                <Badge tone={item.booking_type === 'gr' ? 'accent' : 'neutral'}>
                  {item.booking_type === 'gr' ? 'Grupal' : 'Individual'}
                </Badge>
              </div>
            </header>

            <section className={styles.scoresSection}>
              <h3 className={styles.sectionTitle}>Puntuaciones</h3>
              <ul className={styles.scoresList}>
                {scores.map((s) => (
                  <li key={s.label} className={styles.scoreRow}>
                    <span className={styles.scoreLabel}>{s.label}</span>
                    <span className={styles.scoreValue}>
                      <StarRating value={s.value} size={20} label={s.label} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {item.notes && (
              <section className={styles.notesSection}>
                <h3 className={styles.sectionTitle}>Notas del profe</h3>
                <p className={styles.notesText}>{item.notes}</p>
              </section>
            )}
          </>
        )}
      </main>
    </>
  );
}
